/**
 * Hook del que cuelgan todas las pantallas de listado — portado de PartnersV2
 * (arquitectura hermana, mismo problema: paginación/orden/búsqueda resueltos en el
 * SERVIDOR, nunca cargando todo al navegador). Ver PLAN.md Fase 11.
 *
 * Descarta respuestas que llegan fuera de orden (si el usuario teclea rápido en el
 * buscador, la respuesta de "ABC" podría llegar después que la de "ABCD").
 *
 * @module hooks/useListado
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

function useDebounce(valor, ms = 350) {
  const [retrasado, setRetrasado] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setRetrasado(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return retrasado;
}

/**
 * @param {Object} opciones
 * @param {(params: Object, opts: {signal: AbortSignal}) => Promise<{data, meta}>} opciones.listar
 *        Recibe `opts.signal` (AbortController) para cancelar la petición anterior en
 *        vuelo cuando se lanza una nueva antes de que responda -- pasarlo a
 *        axios/apiClient como `{ signal: opts.signal }`.
 * @param {Object} [opciones.filtrosIniciales]
 * @param {string} [opciones.ordenPor='createdAt']
 * @param {'asc'|'desc'} [opciones.orden='desc']
 * @param {number} [opciones.porPagina=20]
 */
export function useListado({ listar, filtrosIniciales = {}, ordenPor = 'createdAt', orden = 'desc', porPagina = 20 }) {
  const [filas, setFilas] = useState([]);
  const [paginacion, setPaginacion] = useState({ page: 1, limit: porPagina, totalDocs: 0, totalPages: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [pagina, setPagina] = useState(1);
  const [sortBy, setSortBy] = useState(ordenPor);
  const [sortOrder, setSortOrder] = useState(orden);
  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [busqueda, setBusqueda] = useState('');

  const busquedaRetrasada = useDebounce(busqueda);
  const secuencia = useRef(0);
  const abortRef = useRef(null);

  const params = useMemo(
    () => ({ ...filtros, search: busquedaRetrasada || undefined, page: pagina, limit: porPagina, sortBy, sortOrder }),
    [filtros, busquedaRetrasada, pagina, porPagina, sortBy, sortOrder]
  );

  const cargar = useCallback(async () => {
    const mia = ++secuencia.current;
    // Cancela la petición anterior en vuelo (si el usuario ya tecleó/cambió filtros de
    // nuevo) para no dejarla completarse en el backend sin necesidad -- la guarda de
    // `secuencia` ya evitaba que su respuesta pisara el estado, pero no la cancelaba.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setCargando(true);
    setError(null);
    try {
      const resp = await listar(params, { signal: controller.signal });
      if (mia !== secuencia.current) return;
      setFilas(resp?.data ?? []);
      setPaginacion(resp?.meta?.pagination ?? { page: 1, limit: porPagina, totalDocs: 0, totalPages: 0 });
    } catch (e) {
      if (mia !== secuencia.current || axios.isCancel(e) || e.code === 'ERR_CANCELED') return;
      setError(e.message ?? 'No se pudo cargar la información');
      setFilas([]);
    } finally {
      if (mia === secuencia.current) setCargando(false);
    }
  }, [listar, params, porPagina]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const ordenarPor = useCallback(
    (columna) => {
      setPagina(1);
      if (columna === sortBy) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      else {
        setSortBy(columna);
        setSortOrder('desc');
      }
    },
    [sortBy]
  );

  const cambiarFiltros = useCallback((cambios) => {
    setPagina(1);
    setFiltros((f) => ({ ...f, ...cambios }));
  }, []);

  return {
    filas, paginacion, cargando, error,
    pagina, setPagina,
    sortBy, sortOrder, ordenarPor,
    filtros, cambiarFiltros,
    busqueda, setBusqueda,
    recargar: cargar,
  };
}

export default useListado;
